import {
  ChargeCardAccountDetailPaymentGroup,
  IAccountDetailItem,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useAccountDetails from 'features/accounts/hooks/useAccountDetails';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Dialog from 'shared/components/vxl/Dialog/Dialog';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const CreditBuildingScreen = () => {
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();
  const { t: translatedText } = useTranslation('believe', {
    keyPrefix: 'creditBuildingDetails',
  });
  const { id } = useLocalSearchParams<{
    id: string;
  }>();
  const { accountDetails, errorModel, loading } = useAccountDetails(id);
  const [dialogMessage, setDialogMessage] = useState('');
  const [dialogVisible, setDialogVisible] = useState(false);
  const creditBuildingItems = accountDetails?.details?.flatMap(
    (item: ChargeCardAccountDetailPaymentGroup) => {
      return item?.creditBuildingItems as IAccountDetailItem[];
    },
  );

  const onPressManageAccountItem = (item: IAccountDetailItem) => {
    const showDialog =
      !!item.description && !item.itemDetail && !item.articleLink;
    if (showDialog) {
      setDialogMessage(item?.description ?? '');
      setDialogVisible(true);
      return;
    }
  };

  const okButton = [
    {
      label: translatedText('ok'),
      onClick: () => {
        setDialogVisible(false);
      },
    },
  ];

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: translatedText('creditBuildingDetails'),
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <>
        <View style={styles.titleContainer}>
          <BodyBaseText numberOfLines={0}>
            {translatedText('noActionNeeded')}{' '}
            <BodyBaseBoldText numberOfLines={0}>
              {translatedText('safePayOnText')}
            </BodyBaseBoldText>
            {translatedText('creditBureaus')}
          </BodyBaseText>
        </View>
        {creditBuildingItems?.map((value) => (
          <View key={`childrenKey${value.key}`}>
            <ListItem
              secondaryCopy={value.title}
              primaryCopy={value.value}
              lineFormat={ListItemLineFormat.LABEL}
              showsChevron={false}
              onPress={() => onPressManageAccountItem(value)}
              trailingAccessory={{
                id: '',
                name: VXLComponentId.ICON,
                props: {
                  iconKey: 'QUESTION_MARK',
                  iconSize: 'MD',
                },
              }}
            />

            <View style={styles.dividerWrapper}>
              <HairLineDivider />
            </View>
          </View>
        ))}
        <ListItem
          onPress={() => {
            router.push({
              pathname: `/believe/payment-history`,
              params: { id },
            });
          }}
          primaryCopy={translatedText('paymentHistory')}
        />
      </>
      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          errorModel && !loading ? <ErrorScreen {...errorModel} /> : null
        }
      />
      <Dialog
        messageText={dialogMessage}
        isDialogVisible={dialogVisible}
        setModalVisible={setDialogVisible}
        dialogButtonProps={okButton}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  dividerWrapper: {
    paddingHorizontal: theme.spacing.m,
  },

  titleContainer: {
    margin: theme.spacing.m,
  },
});

export default CreditBuildingScreen;
